import { computed, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAbortableRequest } from './useAbortableRequest';

const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 100;

export function useWhatsappQrSession({
  createSession,
  getSession,
  onConnected,
}) {
  const { t } = useI18n();
  const { run, abort } = useAbortableRequest();
  const sessionKey = ref('');
  const qrImage = ref('');
  const status = ref('idle');
  const errorMessage = ref('');
  const isIdle = computed(() => status.value === 'idle');
  const isConnected = computed(() => status.value === 'connected');
  const hasError = computed(() => status.value === 'error');
  let pollTimer = null;
  let pollCount = 0;
  let active = true;

  function stopPolling() {
    clearTimeout(pollTimer);
    pollTimer = null;
    abort();
  }

  function resetSession() {
    stopPolling();
    sessionKey.value = '';
    qrImage.value = '';
    status.value = 'idle';
    errorMessage.value = '';
  }

  async function pollSession() {
    try {
      const response = await run(signal =>
        getSession(sessionKey.value, signal)
      );
      if (!active || !response) return;
      const { data } = response;
      if (data.status === 'connected') {
        await onConnected?.(data);
        if (!active) return;
        status.value = 'connected';
        qrImage.value = '';
        return;
      }
      if (data.status === 'error') throw new Error();
      status.value = data.status === 'qr' ? 'qr' : 'connecting';
      qrImage.value = data.qr || '';
      pollCount += 1;
      if (pollCount >= MAX_POLLS) {
        status.value = 'error';
        errorMessage.value = t('OTP_MESSAGES.QR_TIMEOUT');
        return;
      }
      pollTimer = setTimeout(pollSession, POLL_INTERVAL_MS);
    } catch {
      if (!active) return;
      status.value = 'error';
      errorMessage.value = t('OTP_MESSAGES.QR_ERROR');
    }
  }

  async function generateQr() {
    stopPolling();
    status.value = 'connecting';
    qrImage.value = '';
    errorMessage.value = '';
    pollCount = 0;
    try {
      const response = await run(signal => createSession(signal));
      if (!active || !response) return;
      sessionKey.value = response.data.session_key;
      await pollSession();
    } catch {
      if (!active) return;
      status.value = 'error';
      errorMessage.value = t('OTP_MESSAGES.QR_ERROR');
    }
  }

  onBeforeUnmount(() => {
    active = false;
    stopPolling();
  });

  return {
    sessionKey,
    qrImage,
    status,
    errorMessage,
    isIdle,
    isConnected,
    hasError,
    generateQr,
    resetSession,
    stopPolling,
  };
}
